/**
 * Iter 55 UI — H2H Matchup Chip.
 *
 * Surfaces the H2H "matchup pattern" from `/api/gold/h2h/:a/:b` right in
 * the Match Dossier so users can SEE the H2H work applied to their pick:
 *
 *  • Dominance score + long-term trend pill (home_dominant / balanced / away_dominant)
 *  • Recent-trend delta arrow (recent home_wr vs long-term)
 *  • Venue split visualisation (home team's H2H record when playing at home)
 *  • "Adjustments applied" block — the actual pp/xG shifts each engine
 *    consumed, plus a click-to-expand audit trail from `h2h_engine`.
 *  • Conflict banner when H2H disagrees with current form (dampened 50%).
 *
 * Backed by the same `h2h_engine` payload the prediction algorithms
 * consume, so what the user sees IS what the model saw.
 */
import { useState } from "react";
import { Zap, TrendingUp, TrendingDown, Minus, MapPin, AlertTriangle, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const pct = (v, digits = 0) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(digits)}%`;
};

const signed = (v, digits = 2, suffix = "") => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const n = Number(v);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}${suffix}`;
};

const clampPct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

const TrendPill = ({ trend, dominance }) => {
  const label =
    trend === "home_dominant" ? "Home dominant" :
    trend === "away_dominant" ? "Away dominant" :
    trend === "balanced" ? "Balanced" : "No pattern";
  const isHome = trend === "home_dominant";
  const isAway = trend === "away_dominant";
  const isBalanced = trend === "balanced";
  const cls = isHome
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
    : isAway
    ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
    : isBalanced
    ? "bg-amber-500/15 text-amber-200 border-amber-500/40"
    : "bg-slate-500/15 text-slate-300 border-slate-500/40";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${cls}`}
      data-testid="h2h-chip-trend-pill"
    >
      <Zap className="w-3 h-3" />
      {label}
      <span className="opacity-75 font-mono">· {signed(dominance, 0)}</span>
    </span>
  );
};

const TrendArrow = ({ deltaPp }) => {
  const n = Number(deltaPp || 0);
  if (Math.abs(n) < 0.5) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="h2h-chip-recent-neutral">
        <Minus className="w-3 h-3" /> Recent {signed(n, 1, "pp")}
      </span>
    );
  }
  const Icon = n > 0 ? TrendingUp : TrendingDown;
  const cls = n > 0 ? "text-emerald-300" : "text-rose-300";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${cls}`} data-testid="h2h-chip-recent-arrow">
      <Icon className="w-3 h-3" />
      Recent {signed(n, 1, "pp")}{n > 0 ? " toward home" : " toward away"}
    </span>
  );
};

const VenueBar = ({ splits, homeName, awayName }) => {
  const n = Number(splits?.home_venue_matches || 0);
  if (n === 0) {
    return (
      <div className="text-[11px] text-slate-400" data-testid="h2h-chip-venue-empty">
        No venue history at {homeName}
      </div>
    );
  }
  const hw = Number(splits?.home_venue_home_wins || 0);
  const aw = Number(splits?.home_venue_away_wins || 0);
  const dr = Number(splits?.home_venue_draws || 0);
  const total = hw + aw + dr || 1;
  const hwPct = clampPct((hw / total) * 100);
  const drPct = clampPct((dr / total) * 100);
  const awPct = clampPct((aw / total) * 100);
  return (
    <div className="space-y-1" data-testid="h2h-chip-venue-bar">
      <div className="flex items-center justify-between text-[10px] text-slate-400">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> At {homeName} · {n} meeting{n === 1 ? "" : "s"}
        </span>
        <span className="font-mono text-slate-300">{hw}W {dr}D {aw}L</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-slate-800 flex">
        <div className="bg-emerald-500" style={{ width: `${hwPct}%` }} title={`${homeName} ${hwPct.toFixed(0)}%`} />
        <div className="bg-slate-500" style={{ width: `${drPct}%` }} title={`Draws ${drPct.toFixed(0)}%`} />
        <div className="bg-rose-500" style={{ width: `${awPct}%` }} title={`${awayName} ${awPct.toFixed(0)}%`} />
      </div>
      <div className="text-[10px] text-slate-500">
        Avg goals: {(splits?.home_venue_avg_home_goals ?? 0).toFixed(2)} — {(splits?.home_venue_avg_away_goals ?? 0).toFixed(2)}
      </div>
    </div>
  );
};

const AdjustmentPill = ({ label, value, unit = "pp", positiveIsGood = null, testid }) => {
  const n = Number(value);
  if (!Number.isFinite(n) || Math.abs(n) < 0.01) return null;
  // Colour: neutral by default. If positiveIsGood is true → green when +,
  // red when −. If false → inverse. If null → neutral amber.
  let cls = "bg-amber-500/10 text-amber-200 border-amber-500/30";
  if (positiveIsGood === true) {
    cls = n > 0 ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-rose-500/10 text-rose-300 border-rose-500/30";
  } else if (positiveIsGood === false) {
    cls = n > 0 ? "bg-rose-500/10 text-rose-300 border-rose-500/30"
                : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono font-medium ${cls}`}
      data-testid={testid}
      title={`${label}: ${signed(n, 2, unit)}`}
    >
      <span className="uppercase tracking-wide font-sans font-semibold">{label}</span>
      <span>{signed(n, 2, unit)}</span>
    </span>
  );
};

const ConfidenceBar = ({ value }) => {
  const p = clampPct(Number(value || 0) * 100);
  return (
    <div className="flex items-center gap-2" data-testid="h2h-chip-confidence">
      <span className="text-[10px] uppercase tracking-widest text-slate-400">Signal</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-800">
        <div
          className={`h-full ${p >= 66 ? "bg-emerald-500" : p >= 33 ? "bg-amber-500" : "bg-slate-500"}`}
          style={{ width: `${p}%` }}
        />
      </div>
      <span className="text-[10px] font-mono text-slate-300 w-8 text-right">{p.toFixed(0)}%</span>
    </div>
  );
};

/**
 * H2HMatchupChip — main export.
 *
 * @param {object} h2h — the payload from `/api/gold/h2h/:a/:b` (v2 shape, iter 55)
 * @param {string} homeName
 * @param {string} awayName
 */
export const H2HMatchupChip = ({ h2h, homeName = "Home", awayName = "Away" }) => {
  const [expanded, setExpanded] = useState(false);

  if (!h2h || h2h.status === "no_data" || h2h.status === "engine_error") {
    return (
      <div
        className="rounded-xl border border-amber-500/20 bg-black/50 p-3.5 text-[11px] text-amber-500/60"
        data-testid="h2h-chip-empty"
      >
        <Zap className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
        Matchup pattern: {h2h?.status === "engine_error" ? "engine error" : "no prior meetings on record"}
      </div>
    );
  }

  const meetings = Number(h2h?.meetings || 0);
  const trend = h2h?.long_term_trend || "no_evidence";
  const dominance = Number(h2h?.dominance_score || 0);
  const recentDelta = Number(h2h?.recent_trend_delta_home_pp || 0);
  const venueSplits = h2h?.venue_splits || {};
  const seasonSplits = h2h?.season_splits || {};
  const adj = h2h?.adjustments_applied || {};
  const confidence = Number(h2h?.confidence || 0);
  const conflictFlag = Boolean(adj?.conflict_flag);
  const conflictNote = adj?.conflict_note;
  const audit = Array.isArray(adj?.audit) ? adj.audit : [];

  const oneXTwo = adj?.one_x_two_pp || { home: 0, draw: 0, away: 0 };
  const xg = adj?.xg_goals || { home: 0, away: 0 };
  const btts = Number(adj?.btts_yes_pp || 0);
  const o25 = Number(adj?.over_2_5_pp || 0);
  const htO05 = Number(adj?.ht_over_0_5_pp || 0);
  const htBtts = Number(adj?.ht_btts_pp || 0);

  const hasAnyAdjustment =
    Math.abs(oneXTwo.home) + Math.abs(oneXTwo.draw) + Math.abs(oneXTwo.away) +
    Math.abs(xg.home) + Math.abs(xg.away) +
    Math.abs(btts) + Math.abs(o25) + Math.abs(htO05) + Math.abs(htBtts) > 0.005;

  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-950/40 via-black to-amber-950/20 p-3.5 space-y-3"
      data-testid="h2h-matchup-chip"
    >
      {/* Header: trend pill + recent-trend arrow + confidence */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <TrendPill trend={trend} dominance={dominance} />
          <TrendArrow deltaPp={recentDelta} />
        </div>
        <div className="text-[10px] text-amber-500/70 font-mono" data-testid="h2h-chip-sample">
          {meetings} meeting{meetings === 1 ? "" : "s"} analysed
        </div>
      </div>

      <ConfidenceBar value={confidence} />

      {/* Conflict warning */}
      {conflictFlag && conflictNote && (
        <div
          className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-950/30 p-2.5"
          data-testid="h2h-chip-conflict"
        >
          <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-rose-200 leading-snug">
            <div className="font-semibold uppercase tracking-wider text-rose-300 mb-0.5">H2H vs Form Conflict</div>
            {conflictNote}
            <div className="text-[10px] text-rose-400/80 mt-1">Adjustment magnitude dampened 50% by the engine.</div>
          </div>
        </div>
      )}

      {/* Venue split */}
      <VenueBar splits={venueSplits} homeName={homeName} awayName={awayName} />

      {/* Season splits — only render when we have data to show */}
      {(seasonSplits.prev_season_matches > 0 || seasonSplits.prev_two_seasons_matches > 0) && (
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-amber-500/10">
          <div className="text-[10px]" data-testid="h2h-chip-season-prev">
            <div className="uppercase tracking-widest text-amber-500/60">Previous season</div>
            <div className="font-mono text-amber-100 mt-0.5">
              {seasonSplits.prev_season_matches} meeting{seasonSplits.prev_season_matches === 1 ? "" : "s"}
              {seasonSplits.prev_season_matches > 0 && (
                <> · {(seasonSplits.prev_season_avg_goals ?? 0).toFixed(2)} g/g</>
              )}
            </div>
          </div>
          <div className="text-[10px]" data-testid="h2h-chip-season-prev2">
            <div className="uppercase tracking-widest text-amber-500/60">Previous 2 seasons</div>
            <div className="font-mono text-amber-100 mt-0.5">
              {seasonSplits.prev_two_seasons_matches} meeting{seasonSplits.prev_two_seasons_matches === 1 ? "" : "s"}
              {seasonSplits.prev_two_seasons_matches > 0 && (
                <> · {(seasonSplits.prev_two_seasons_avg_goals ?? 0).toFixed(2)} g/g</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Adjustments applied */}
      <div className="pt-2 border-t border-amber-500/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold">
              Adjustments applied
            </span>
          </div>
          {audit.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] text-amber-500/70 hover:text-amber-300 transition"
              data-testid="h2h-chip-audit-toggle"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide" : "See"} audit ({audit.length})
            </button>
          )}
        </div>

        {!hasAnyAdjustment ? (
          <div className="text-[11px] text-slate-400" data-testid="h2h-chip-no-adjust">
            Sample too small — no adjustment applied to the prediction.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="h2h-chip-adjustments">
            <AdjustmentPill label="1X2 H" value={oneXTwo.home} unit="pp" positiveIsGood={true}  testid="h2h-adj-1x2-h" />
            <AdjustmentPill label="1X2 D" value={oneXTwo.draw} unit="pp" positiveIsGood={null}  testid="h2h-adj-1x2-d" />
            <AdjustmentPill label="1X2 A" value={oneXTwo.away} unit="pp" positiveIsGood={false} testid="h2h-adj-1x2-a" />
            <AdjustmentPill label="xG H"  value={xg.home}      unit=" g" positiveIsGood={true}  testid="h2h-adj-xg-h" />
            <AdjustmentPill label="xG A"  value={xg.away}      unit=" g" positiveIsGood={false} testid="h2h-adj-xg-a" />
            <AdjustmentPill label="BTTS"  value={btts}          unit="pp" positiveIsGood={null}  testid="h2h-adj-btts" />
            <AdjustmentPill label="O2.5"  value={o25}           unit="pp" positiveIsGood={null}  testid="h2h-adj-o25" />
            <AdjustmentPill label="HT O0.5" value={htO05}       unit="pp" positiveIsGood={null}  testid="h2h-adj-ht-o05" />
            <AdjustmentPill label="HT BTTS" value={htBtts}      unit="pp" positiveIsGood={null}  testid="h2h-adj-ht-btts" />
          </div>
        )}

        {expanded && audit.length > 0 && (
          <ul
            className="mt-2.5 space-y-1 text-[10px] font-mono text-amber-500/70 bg-black/50 rounded-md p-2 border border-amber-500/10 max-h-40 overflow-y-auto"
            data-testid="h2h-chip-audit-list"
          >
            {audit.map((line, i) => (
              <li key={i} className="leading-relaxed">
                <span className="text-amber-500/40 mr-1.5">›</span>{line}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default H2HMatchupChip;
